import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { DAALUU_FLIGHT_MS, DAALUU_HOUSE_FLIGHT_MS, DAALUU_PLAY_MS, daaluuMotionTiming, type DaaluuMovement, type DaaluuTile as Tile, type DaaluuPlay, type GameView } from '@big2/shared';
import { DaaluuTile } from './DaaluuTile.js';
import { playTilePlaceSound } from '../../../sound.js';

interface Point { x: number; y: number }
interface Flight { key: string; tile: Tile; pile?: Tile[]; from: Point; to: Point; revealAt: Point; reveal: boolean; dead: boolean; duration: number; delay: number; leaving: boolean; capture: boolean }

function center(element: Element | null): Point | null {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

/**
 * Where a flight starts. The hand-slot cache only holds the viewer's own
 * tiles, and tile IDs are stable across rounds, so it must never position
 * another seat's tiles: a stale entry would launch an opponent's tile from
 * the viewer's own hand instead of that opponent's seat.
 */
export function daaluuFlightFrom(
  move: Pick<DaaluuMovement, 'from' | 'fromSeat'>,
  tileId: string,
  mySeat: number,
  handPositions: ReadonlyMap<string, Point>,
  boardPositions: ReadonlyMap<string, Point>,
  boardPoint: Point,
  seatHandPoint: (seat: number) => Point | null,
): Point | null {
  if (move.from === 'board') return boardPositions.get(tileId) ?? boardPoint;
  if (move.from === 'capture') return null;
  if (move.fromSeat === mySeat) return handPositions.get(tileId) ?? seatHandPoint(mySeat);
  return seatHandPoint(move.fromSeat);
}

/** Only server-published tiles can fly. Mounting/reconnecting never replays history. */
export function useDaaluuMotion(game: GameView | null) {
  const d = game?.daaluu;
  const seen = useRef(`${d?.shuffle.roundId}:${d?.motion?.serial}`);
  const previousHandTiles = useRef(new Set(d?.hand.map(tile => tile.id) ?? []));
  const previousTiles = useRef(new Map<string, Point>());
  const rememberHandPositions = useCallback(() => {
    const positions = new Map(previousTiles.current);
    document.querySelectorAll<HTMLElement>('[data-daaluu-hand-tile]').forEach(element => {
      const parent = element.offsetParent as HTMLElement | null;
      const base = parent?.getBoundingClientRect();
      // Reordering animates wrappers with transforms; cache their settled slots.
      const point = base && parent ? {
        x: base.left + parent.clientLeft + element.offsetLeft + element.offsetWidth / 2,
        y: base.top + parent.clientTop + element.offsetTop + element.offsetHeight / 2,
      } : center(element);
      if (point) positions.set(element.getAttribute('data-daaluu-hand-tile')!, point);
    });
    previousTiles.current = positions;
  }, []);
  const previousBoard = useRef(new Map<string, Point>());
  const previousCaptures = useRef(new Map<string, Point>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const queueUntil = useRef(0);
  const [batches, setBatches] = useState<{ key: string; flights: Flight[]; arriving: string[]; boardArriving: string[]; boardLeaving: string[]; trick: { winnerSeat: number; plays: DaaluuPlay[] } | null }[]>([]);
  useLayoutEffect(() => {
    if (!game || !d) return;
    const epoch = `${d.shuffle.roundId}:${d.motion?.serial}`;
    if (seen.current !== epoch) {
      const sameRound = seen.current.startsWith(`${d.shuffle.roundId}:`);
      seen.current = epoch;
      if (!sameRound) {
        timers.current.forEach(clearTimeout); timers.current.clear(); queueUntil.current = 0; setBatches([]);
      }
      const board = center(document.querySelector('[data-daaluu-motion-board]'));
      const seatPoint = (seat: number, target: 'hand' | 'capture') => center(document.querySelector(
        target === 'hand' && seat === game.mySeat ? '[data-daaluu-motion-hand]' : `[data-daaluu-collection-seat="${seat}"] ${target === 'capture' ? '.daaluu-seat-captures' : '.daaluu-seat-badge'}`));
      const flights: Flight[] = [];
      const publicPlays = [...d.trick, ...(d.lastTrick?.plays ?? [])];
      const isPlayedTile = (id: string) => publicPlays.some(play => play.tiles.some(tile => tile.id === id));
      const isDeadTile = (id: string) => publicPlays.some(play => play.deadTileIds?.includes(id));
      const phaseDuration = d.motion ? daaluuMotionTiming(d).phaseMs : DAALUU_FLIGHT_MS;
      if (sameRound && board) for (const [index, move] of (d.motion?.movements ?? []).entries()) {
        if (move.from === 'capture' && move.to === 'capture' && move.tiles.length) {
          const pileId = move.tiles[0].id;
          const from = previousCaptures.current.get(pileId) ?? seatPoint(move.fromSeat, 'capture');
          const to = center(document.querySelector(`[data-daaluu-capture-house="${pileId}"]`)) ?? seatPoint(move.toSeat, 'capture');
          if (from && to) flights.push({ key: `${epoch}:${index}:house`, tile: move.tiles[0], pile: move.tiles,
            from, to, revealAt: board, reveal: false, dead: false, duration: DAALUU_HOUSE_FLIGHT_MS,
            delay: move.phase === 2 ? phaseDuration + DAALUU_FLIGHT_MS : move.phase * phaseDuration,
            leaving: true, capture: true });
          continue;
        }
        move.tiles.forEach((tile, tileIndex) => {
          // A repeated board-to-hand movement must not hide/replay a tile that
          // was already in this viewer's hand before the new motion arrived.
          if (move.to === 'hand' && move.toSeat === game.mySeat && previousHandTiles.current.has(tile.id)) return;
          // Keep exchange rows stable when a later phase repeats the same
          // hand-to-board movement for tiles already shown in the reveal.
          if (move.to === 'board' && previousBoard.current.has(tile.id)) return;
          const offset = (tileIndex - (move.tiles.length - 1) / 2) * 25;
          const boardPoint = center(document.querySelector(`[data-daaluu-board-tile="${tile.id}"]`)) ?? { x: board.x + offset, y: board.y + (move.fromSeat === d.tribute?.slaveSeat ? -30 : 30) };
          const from = daaluuFlightFrom(move, tile.id, game.mySeat, previousTiles.current, previousBoard.current, boardPoint,
            seat => seatPoint(seat, 'hand'));
          const to = move.to === 'board' ? boardPoint : seatPoint(move.toSeat, move.to);
          const reveal = move.to === 'board' && isPlayedTile(tile.id);
          if (from && to) flights.push({ key: `${epoch}:${index}:${tile.id}`, tile, from, to,
            revealAt: { x: Math.max(40, Math.min(window.innerWidth - 40, board.x + offset * 2.5)), y: Math.max(85, Math.min(window.innerHeight - 85, board.y - 45)) }, reveal, dead: isDeadTile(tile.id),
            duration: reveal ? DAALUU_PLAY_MS : DAALUU_FLIGHT_MS,
            delay: move.phase === 2 ? phaseDuration + DAALUU_FLIGHT_MS : move.phase * phaseDuration,
            leaving: move.to !== 'board', capture: move.to === 'capture' });
        });
      }
      if (flights.length) {
        const now = Date.now();
        const masterReturnReveal = d.stage === 'tribute-reveal' && d.tributeBatch?.phase === 'master-return';
        const wait = masterReturnReveal ? 0 : Math.max(0, queueUntil.current - now);
        const duration = Math.max(...flights.map(flight => flight.delay + flight.duration));
        queueUntil.current = now + wait + duration;
        const batch = { key: epoch, boardArriving: flights.filter(flight => !flight.leaving).map(flight => flight.tile.id), boardLeaving: [] as string[], trick: flights.some(flight => flight.capture && !flight.pile) ? d.lastTrick : null, flights: flights.map(flight => ({ ...flight, delay: flight.delay + wait })), arriving: (d.motion?.movements ?? [])
          .filter(move => move.to === 'hand' && move.toSeat === game.mySeat).flatMap(move => move.tiles.map(tile => tile.id))
          .filter(tileId => !previousHandTiles.current.has(tileId)) };
        setBatches(current => [...current, batch]);
        for (const [index, move] of (d.motion?.movements ?? []).entries()) {
          if (move.from !== 'hand' || move.to !== 'board') continue;
          const soundKey = `${epoch}:sound:${index}`;
          const reveal = move.tiles.some(tile => isPlayedTile(tile.id));
          const impact = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : reveal ? DAALUU_PLAY_MS * .28 : DAALUU_FLIGHT_MS;
          timers.current.set(soundKey, setTimeout(() => {
            timers.current.delete(soundKey); playTilePlaceSound(move.tiles.length);
          }, wait + move.phase * phaseDuration + impact));
        }
        timers.current.set(epoch, setTimeout(() => {
          timers.current.delete(epoch); setBatches(current => current.filter(batch => batch.key !== epoch));
        }, wait + duration));
        if (batch.boardArriving.length) {
          const placementDuration = Math.max(...flights.filter(flight => !flight.leaving).map(flight => flight.delay + flight.duration));
          const placedKey = `${epoch}:placed`;
          timers.current.set(placedKey, setTimeout(() => {
            timers.current.delete(placedKey); setBatches(current => current.map(item => item.key === epoch ? { ...item, boardArriving: [] } : item));
          }, wait + placementDuration));
        }
        if (batch.trick) {
          const collectKey = `${epoch}:collecting`;
          timers.current.set(collectKey, setTimeout(() => {
            timers.current.delete(collectKey); setBatches(current => current.map(batch => batch.key === epoch ? { ...batch, boardLeaving: flights.filter(flight => flight.capture).map(flight => flight.tile.id) } : batch));
          }, wait + phaseDuration));
        }
      }
    }
    rememberHandPositions();
    previousHandTiles.current = new Set(d.hand.map(tile => tile.id));
    const boardPositions = new Map<string, Point>();
    document.querySelectorAll('[data-daaluu-board-tile]').forEach(element => {
      const point = center(element);
      if (point) boardPositions.set(element.getAttribute('data-daaluu-board-tile')!, point);
    });
    previousBoard.current = boardPositions;
    const capturePositions = new Map<string, Point>();
    document.querySelectorAll('[data-daaluu-capture-house]').forEach(element => {
      const point = center(element), id = element.getAttribute('data-daaluu-capture-house');
      if (point && id) capturePositions.set(id, point);
    });
    previousCaptures.current = capturePositions;
  }, [game, d]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current.clear(); }, []);
  const flights = batches.flatMap(batch => batch.flights);
  const pendingBoardArriving = d?.motion && seen.current.startsWith(`${d.shuffle.roundId}:`) &&
    seen.current !== `${d.shuffle.roundId}:${d.motion.serial}`
    ? d.motion.movements.filter(move => move.to === 'board').flatMap(move => move.tiles.map(tile => tile.id))
      .filter(tileId => !previousBoard.current.has(tileId)) : [];
  return { flights, rememberHandPositions, arriving: batches.flatMap(batch => batch.arriving), boardArriving: [...pendingBoardArriving, ...batches.flatMap(batch => [...batch.boardArriving, ...batch.boardLeaving])],
    capturing: flights.filter(flight => flight.capture).flatMap(flight => (flight.pile ?? [flight.tile]).map(tile => tile.id)),
    settlingTrick: batches.find(batch => batch.trick)?.trick };
}

export function DaaluuMotion({ flights, mn }: { flights: Flight[]; mn: boolean }) {
  return flights.length ? createPortal(<div className="daaluu-motion-layer" aria-hidden="true">
    {flights.map(flight =>
    <span key={flight.key} className={`daaluu-tile-flight${flight.leaving ? ' is-leaving' : ''}${flight.reveal ? ' is-play' : ''}${flight.pile ? ' is-house-transfer' : ''}`} style={{
      '--from-x': `${flight.from.x}px`, '--from-y': `${flight.from.y}px`, '--to-x': `${flight.to.x}px`, '--to-y': `${flight.to.y}px`,
      '--reveal-x': `${flight.revealAt.x}px`, '--reveal-y': `${flight.revealAt.y}px`,
      animationDelay: `${flight.delay}ms`, animationDuration: `${flight.duration}ms`,
    } as CSSProperties}>{flight.pile
      ? <span className="daaluu-house-flight">{flight.pile.map((tile, index) => <span className="daaluu-pile-layer" key={tile.id}
        style={{ '--layer': index } as CSSProperties}><DaaluuTile tile={tile} /></span>)}</span>
      : <DaaluuTile tile={flight.tile} dead={flight.dead} deadLabel={mn ? 'Үхсэн' : 'Dead'} />}</span>)}</div>, document.body) : null;
}
